import Services from 'store/Services';
import State from 'store/State';
import { ResetStateAction, resetStateAction } from 'store/rootActions';
import LoginResponse from 'communication/LoginResponse';
import { mapLoginRequestError } from 'login/loginRequestMapper';

import { ThunkDispatch } from 'store/store';
import {
    LoginStateChangeAction,
    loginStatePendingAction,
    loginStateSuccessAction,
    loginStateErrorAction,
} from './actions';

import { buildLoggerForStore } from 'logging/writers/utils';
import omit from 'lodash/omit';
import { setMachineId } from 'app/store/general/actions';
import {
    setFeatureToggles,
    FeatureToggleStateAction,
} from 'app/store/featureToggles/actions';
import {
    setConnected,
    SetConnectedAction,
    SetDisconnectedAction,
} from 'app/store/connectivity/actions';
import IndigoLoginError from 'communication/IndigoLoginError';
import { JwtLoginOptions } from 'communication/IndigoApi';
import {
    setIndigoMarketDataEnabled,
    SetIndigoMarketDataEnabled,
} from 'marketData/store/marketDataSlice';
import StreamDataService from 'streamsState/StreamDataService.ts';

const log = buildLoggerForStore('login');

export type LoginActionThunk = (
    dispatch: ThunkDispatch<LoginStateChangeAction>,
    _getState: () => State,
    { loginService }: Services
) => Promise<void>;

export const loginAction: (
    userName: string,
    password: string
) => LoginActionThunk =
    (username, password) =>
    async (
        dispatch: ThunkDispatch<LoginStateChangeAction>,
        _getState: () => State,
        { loginService, loggingService, streamDataService }: Services
    ) => {
        log(loggingService, 'loginAction', { username });
        return callLogin(
            dispatch,
            () => loginService.login(username, password),
            streamDataService
        );
    };

export const loginKeyAction =
    (key: string) =>
    async (
        dispatch: ThunkDispatch<LoginStateChangeAction>,
        _getState: () => State,
        { loginService, loggingService, streamDataService }: Services
    ) => {
        log(loggingService, 'loginKeyAction', { key });
        return callLogin(
            dispatch,
            () => loginService.loginWithKey(key),
            streamDataService
        );
    };

export const loginJwtAction =
    (options: JwtLoginOptions) =>
    async (
        dispatch: ThunkDispatch<LoginStateChangeAction>,
        _getState: () => State,
        { loginService, loggingService, streamDataService }: Services
    ) => {
        log(loggingService, 'loginJwtAction', options);
        return callLogin(
            dispatch,
            () => loginService.loginWithJwtToken(options),
            streamDataService
        );
    };

export const logoutAction =
    () =>
    async (
        dispatch: ThunkDispatch<ResetStateAction>,
        _getState: () => State,
        {
            loginService,
            streamDataService,
            loggingService,
            currentLayoutPersistenceService,
        }: Services
    ) => {
        log(loggingService, 'logoutAction');
        await streamDataService.stopAllStreams();
        await currentLayoutPersistenceService.persistIfDirty();
        dispatch(resetStateAction());
        await loginService.logout();
    };

type SetMachineIdAction = ReturnType<typeof setMachineId>;

async function callLogin(
    dispatch: ThunkDispatch<
        | LoginStateChangeAction
        | FeatureToggleStateAction
        | SetMachineIdAction
        | SetDisconnectedAction
        | SetConnectedAction
        | SetIndigoMarketDataEnabled
    >,
    loginCall: () => Promise<LoginResponse>,
    streamDataService: StreamDataService
) {
    dispatch(loginStatePendingAction());
    try {
        await streamDataService.stopAllStreams();
        const loginResponse = await loginCall();
        const userDetails = omit(loginResponse, 'featureToggles');
        dispatch(loginStateSuccessAction(userDetails));
        dispatch(setConnected());
        const featureToggles = loginResponse.featureToggles;
        if (featureToggles) {
            dispatch(setFeatureToggles(featureToggles));
        }
        if (loginResponse.machineId) {
            dispatch(setMachineId(loginResponse.machineId));
        }
        if (loginResponse.indigoMarketDataEnabled !== undefined) {
            dispatch(
                setIndigoMarketDataEnabled(
                    !!loginResponse.indigoMarketDataEnabled
                )
            );
        }
    } catch (err) {
        const loginError = mapLoginRequestError(err as IndigoLoginError);
        dispatch(loginStateErrorAction(loginError));
    }
}
